const LANGUAGE_TO_LOCALE = {
  de: "de-DE",
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR",
  se: "sv-SE",
  sv: "sv-SE",
};

export const getLocaleForLanguage = (language) =>
  LANGUAGE_TO_LOCALE[language] || "fr-FR";

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getSignedError = (aiMassPercent, manualMassPercent) => {
  if (aiMassPercent == null || manualMassPercent == null) return null;
  return aiMassPercent - manualMassPercent;
};

export const formatLocaleNumber = (value, locale, digits = 2) => {
  const parsed = toFiniteNumber(value);
  if (parsed == null) return null;
  return parsed.toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

export const formatPercentValue = (value, locale, digits = 2) => {
  const formatted = formatLocaleNumber(value, locale, digits);
  return formatted == null ? null : `${formatted} %`;
};

const formatSignedPointsValue = (value, locale, digits = 1) => {
  const parsed = toFiniteNumber(value);
  if (parsed == null) return null;
  const formattedAbs = Math.abs(parsed).toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  if (parsed > 0) return `+${formattedAbs}`;
  if (parsed < 0) return `−${formattedAbs}`;
  return formattedAbs;
};

export const formatSignedPercentValue = (value, locale, digits = 1) => {
  const signed = formatSignedPointsValue(value, locale, digits);
  return signed == null ? null : `${signed} %`;
};

export const mapParentCategoryRowsToComparisonRows = (parentCategoryRows) => {
  if (!Array.isArray(parentCategoryRows)) return [];

  const parsed = parentCategoryRows.map((row, index) => {
    const manualMassPercent = toFiniteNumber(row?.manualMassPercent);
    const aiMassPercent = toFiniteNumber(row?.aiMassPercent);
    const signedError = getSignedError(aiMassPercent, manualMassPercent);
    const absError =
      signedError == null ? toFiniteNumber(row?.error) : Math.abs(signedError);

    return {
      parentCategory: row?.parentCategory ?? "—",
      manualMassPercent,
      aiMassPercent,
      signedError,
      absError,
      sortOrder: toFiniteNumber(row?.sortOrder) ?? index,
      weightedError: 0,
      contributionShare: 0,
    };
  });

  const totalManual = parsed.reduce((sum, row) => {
    return row.manualMassPercent != null && row.manualMassPercent > 0
      ? sum + row.manualMassPercent
      : sum;
  }, 0);

  const withWeights = parsed.map((row) => {
    if (
      totalManual <= 0 ||
      row.manualMassPercent == null ||
      row.manualMassPercent < 0 ||
      row.absError == null
    ) {
      return row;
    }
    return {
      ...row,
      weightedError: (row.manualMassPercent / totalManual) * row.absError,
    };
  });

  const totalWeightedError = withWeights.reduce(
    (sum, row) => sum + row.weightedError,
    0,
  );

  const withContribution = withWeights.map((row) => ({
    ...row,
    contributionShare:
      totalWeightedError > 0
        ? (row.weightedError / totalWeightedError) * 100
        : 0,
  }));

  return withContribution.sort((a, b) => {
    if (b.contributionShare !== a.contributionShare) {
      return b.contributionShare - a.contributionShare;
    }
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return String(a.parentCategory).localeCompare(String(b.parentCategory));
  });
};

export const computeConfrontationKpis = (comparisonRows, wmae) => {
  const rows = Array.isArray(comparisonRows) ? comparisonRows : [];
  const usableAbs = rows.filter((row) => row.absError != null);
  const maxAbsRow = usableAbs.reduce((best, row) => {
    if (!best || row.absError > best.absError) return row;
    return best;
  }, null);

  const topContributor = rows.reduce((best, row) => {
    if (row.weightedError <= 0) return best;
    if (!best || row.weightedError > best.weightedError) return row;
    return best;
  }, null);

  return {
    wmae: toFiniteNumber(wmae),
    maxAbsError: maxAbsRow?.absError ?? null,
    maxAbsErrorCategory: maxAbsRow?.parentCategory ?? null,
    topContributorCategory: topContributor?.parentCategory ?? null,
    topContributorShare: topContributor?.contributionShare ?? null,
  };
};

export const computeListPeriodSummary = (listRows) => {
  const rows = Array.isArray(listRows) ? listRows : [];
  const wmaeValues = rows
    .map((row) => toFiniteNumber(row?.wmae))
    .filter((value) => value != null);

  if (wmaeValues.length === 0) {
    return {
      count: rows.length,
      averageWmae: null,
      bestWmae: null,
      worstWmae: null,
    };
  }

  const total = wmaeValues.reduce((sum, value) => sum + value, 0);
  return {
    count: rows.length,
    averageWmae: total / wmaeValues.length,
    bestWmae: Math.min(...wmaeValues),
    worstWmae: Math.max(...wmaeValues),
  };
};
